import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Pagination,
  TablePagination,
  Tooltip,
  IconButton,
  TextField,
  Skeleton,
} from '@mui/material';
import { formatDateToJerusalem } from '../utils/dateFormatter';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import jwtAxios from '@crema/services/auth/jwt-auth';
import PropTypes from 'prop-types';

const LeadFailureHistoryModal = ({ open, onClose, leadId, isDuplicate = false }) => {
  const [loading, setLoading] = useState(false);
  const [failures, setFailures] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [customerFilter, setCustomerFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  // Reset to first page when opening or switching lead
  useEffect(() => {
    if (open && leadId) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [open, leadId]);

  // Debounce the customer filter to avoid spamming requests
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilter(customerFilter.trim());
      // reset to first page when filter changes
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(handle);
  }, [customerFilter]);

  useEffect(() => {
    if (open && leadId) {
      fetchFailureHistory();
    }
  }, [open, leadId, pagination.page, pagination.limit, debouncedFilter]);

  const fetchFailureHistory = async () => {
    setLoading(true);
    try {
      const endpoint = isDuplicate 
        ? `/lead-history/duplicate/${leadId}`
        : `/lead-history/${leadId}`;
      const response = await jwtAxios.get(endpoint, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: debouncedFilter || undefined,
        },
      });

      if (response.data.status === 200) {
        const dataObj = (response.data && response.data.data) || {};
        const items = Array.isArray(dataObj.failures) ? dataObj.failures : [];
        // Robust total extraction with fallback to items length
        const candidates = [dataObj.total, response.data.totalCount, response.data?.data?.pagination?.total];
        const totalResolved = candidates.find((n) => typeof n === 'number');
        const total = typeof totalResolved === 'number' ? totalResolved : items.length;
        const p = dataObj.page || pagination.page || 1;
        const l = dataObj.limit || pagination.limit || 20;

        setFailures(items);
        setPagination({
          page: p,
          limit: l,
          total,
          totalPages: Math.max(1, Math.ceil((total || l) / (l || 20))),
        });
      }
    } catch (error) {
      console.error('Error fetching failure history:', error);
      setFailures([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10) || 20;
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // With server-side search, we no longer need to filter the current page; keep identity mapping
  const displayedFailures = useMemo(() => failures, [failures]);

  const formatDate = (dateString) => {
    return formatDateToJerusalem(dateString);
  };

  const getTransportColor = (transport) => {
    const colors = {
      customCurl: 'primary',
      customUrl: 'secondary',
      trackbox: 'success',
    };
    return colors[transport] || 'default';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutlineIcon color="error" />
            <Typography variant="h6">
              Lead History
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" gap={2} mb={2} alignItems="center">
          <TextField
            size="small"
            label="Filter by customer"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            placeholder="Type customer name or ID"
            fullWidth
          />
        </Box>
        {loading ? (
          <Box>
            {/* Skeleton table header */}
            <Box display="flex" gap={2} mb={2}>
              <Skeleton variant="rectangular" width={220} height={36} />
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date & Time','Customer','User','Transport','Reason'].map((h) => (
                      <TableCell key={h}><Skeleton variant="text" width={140} /></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={180} /></TableCell>
                      <TableCell><Skeleton variant="text" width={160} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={90} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={280} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : failures.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No failure history found for this lead.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                    <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell><strong>Date & Time</strong></TableCell>
                    <TableCell><strong>Customer</strong></TableCell>
                        <TableCell><strong>User</strong></TableCell>
                    <TableCell><strong>Transport</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedFailures.map((failure) => (
                    <TableRow key={failure.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(failure.attempted_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {failure.customer_name || `ID: ${failure.customer_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={failure.user_email ? 'text.primary' : 'text.secondary'}>
                          {failure.user_email || 'System'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {failure.transport && (
                          <Chip
                            label={failure.transport}
                            size="small"
                            color={getTransportColor(failure.transport)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={failure.reason} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {failure.reason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={(_, newPage) => setPagination((prev) => ({ ...prev, page: newPage + 1 }))}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Rows per page:"
              />
            </Box>

            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                Total failures: {pagination.total} | Visible in page: {displayedFailures.length}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadFailureHistoryModal;

LeadFailureHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isDuplicate: PropTypes.bool,
};

LeadFailureHistoryModal.defaultProps = {
  isDuplicate: false,
};

